import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { agendaApi, customerApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import Modal from '../../components/Modal.jsx';
import Spinner from '../../components/Spinner.jsx';
import { Field, Select, TextInput, TextArea } from '../../components/Field.jsx';
import MonthCalendar, { MonthNav, firstOfMonth } from '../../components/MonthCalendar.jsx';
import { formatDayLong, formatTime, isoDay, fullName } from '../../utils/format.js';
import { DURATIONS, APPOINTMENT_CHANNELS as CHANNELS, PERSON_TONES as DOT_TONES } from '../../constants.js';

/**
 * The last step of a teleprospection call.
 *
 * The prospect said yes, so the telepro places the meeting in a sales diary
 * without leaving the call. It is reached from the qualification form, not from
 * the menu - booking is part of that flow, never a standalone errand.
 *
 * Interaction: pick a DAY on the month, then a TIME inside it. The month shows
 * who is free and how many slots; the day panel shows the actual hours, grouped
 * by sales person.
 */
const BookMeetingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const toMessage = useApiError();
  const [searchParams] = useSearchParams();
  const locale = i18n.resolvedLanguage;

  const [cursor, setCursor] = useState(firstOfMonth());
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [commercialId, setCommercialId] = useState('');

  const [day, setDay] = useState(null); // the date whose panel is open
  const [choice, setChoice] = useState(null); // { commercial, slot }
  const [busy, setBusy] = useState(false);

  const leadId = searchParams.get('leadId') || null;
  const [form, setForm] = useState({
    title: '',
    channel: 'VIDEO',
    location: '',
    notes: '',
    customerId: searchParams.get('customerId') || '',
  });

  const year = cursor.getUTCFullYear();
  const month = cursor.getUTCMonth();
  const from = isoDay(new Date(Date.UTC(year, month, 1)));
  const to = isoDay(new Date(Date.UTC(year, month + 1, 0)));

  const { data: commercials } = useApi(() => agendaApi.commercials(), []);
  const { data: customers } = useApi(() => customerApi.list({ pageSize: 200 }), []);
  const { data: slots, loading, refresh } = useApi(
    () => agendaApi.slots({ from, to, durationMinutes, commercialId: commercialId || undefined }),
    [from, to, durationMinutes, commercialId],
  );

  const toneFor = useMemo(() => {
    const order = (commercials?.items ?? []).map((item) => item.id);
    return (id) => DOT_TONES[Math.max(0, order.indexOf(id)) % DOT_TONES.length];
  }, [commercials]);

  /** The API answers per commercial; the calendar needs it per day. */
  const byDay = useMemo(() => {
    const map = new Map();
    for (const entry of slots?.items ?? []) {
      for (const d of entry.days) {
        if (!map.has(d.date)) map.set(d.date, []);
        map.get(d.date).push({ commercial: entry.commercial, slots: d.slots });
      }
    }
    return map;
  }, [slots]);

  const customer = (customers?.items ?? []).find((item) => item.id === form.customerId);
  const dayEntries = day ? (byDay.get(day) ?? []) : [];

  const openDay = (key) => {
    setDay(key);
    setChoice(null);
    setForm((current) => ({
      ...current,
      title: current.title || (customer ? `RDV - ${customer.companyName}` : t('agenda.bookMeeting')),
    }));
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await agendaApi.book({
        commercialId: choice.commercial.id,
        customerId: form.customerId || null,
        leadId,
        title: form.title,
        startAt: choice.slot.start,
        durationMinutes,
        channel: form.channel,
        location: form.location,
        notes: form.notes,
      });
      toast.success(t('agenda.booked'));
      setDay(null);
      setChoice(null);
      await refresh();
      // Coming from a qualification, the call is over: back to the list.
      if (leadId) navigate('/telepro/leads');
    } catch (err) {
      toast.error(toMessage(err));
      // Someone may have taken the slot while the panel was open.
      if (err.code === 'CONFLICT') {
        setChoice(null);
        refresh();
      }
    } finally {
      setBusy(false);
    }
  };

  /** The month cell: who is free that day and how many slots. */
  const renderDay = (key) => {
    const available = byDay.get(key);
    if (!available?.length) return null;

    const total = available.reduce((sum, entry) => sum + entry.slots.length, 0);

    return (
      <>
        {available.slice(0, 3).map(({ commercial, slots: daySlots }) => (
          <span key={commercial.id} className={`person person--${toneFor(commercial.id)} person--static`}>
            <span className="person__dot" aria-hidden="true" />
            <span className="person__name">{commercial.firstName}</span>
            <span className="person__count">{daySlots.length}</span>
          </span>
        ))}
        {available.length > 3 ? <span className="person person--more">+{available.length - 3}</span> : null}
        <span className="calendar__cta">{t('agenda.seeHours', { count: total })}</span>
      </>
    );
  };

  return (
    <>
      <PageHeader
        title={t('agenda.bookMeeting')}
        subtitle={
          leadId
            ? t('agenda.stepFromQualification', { name: customer?.companyName ?? '' })
            : t('agenda.pickDayHint')
        }
        actions={
          leadId ? (
            <button type="button" className="btn" onClick={() => navigate('/telepro/leads')}>
              {t('agenda.bookLater')}
            </button>
          ) : null
        }
      />

      <Toolbar>
        <MonthNav cursor={cursor} onChange={setCursor} />
        <Select
          value={commercialId}
          onChange={setCommercialId}
          placeholder={t('agenda.allCommercials')}
          options={(commercials?.items ?? []).map((item) => ({ value: item.id, label: fullName(item) }))}
        />
        <Select
          value={String(durationMinutes)}
          onChange={(value) => setDurationMinutes(Number(value))}
          options={DURATIONS.map((minutes) => ({ value: String(minutes), label: `${minutes} ${t('common.minutes')}` }))}
        />
      </Toolbar>

      {loading ? <Spinner /> : <MonthCalendar cursor={cursor} renderDay={renderDay} onDayClick={openDay} />}

      {!loading && byDay.size === 0 ? <p className="muted empty-month">{t('agenda.noSlots')}</p> : null}

      {/* The day panel: real hours, grouped by sales person. */}
      <Modal
        open={Boolean(day)}
        title={day ? formatDayLong(day, locale) : ''}
        onClose={() => {
          setDay(null);
          setChoice(null);
        }}
        width={640}
        footer={
          choice ? (
            <>
              <button type="button" className="btn" onClick={() => setChoice(null)}>
                {t('common.back')}
              </button>
              <button type="button" className="btn btn--primary" onClick={confirm} disabled={busy || !form.title}>
                {busy ? t('common.saving') : t('agenda.book')}
              </button>
            </>
          ) : null
        }
      >
        {day && !choice ? (
          <>
            <p className="modal__section-title">{t('agenda.chooseSlot')}</p>
            {dayEntries.map(({ commercial, slots: daySlots }) => (
              <div key={commercial.id} className="day-person">
                <p className="day-person__name">
                  <span className={`person__dot person__dot--${toneFor(commercial.id)}`} aria-hidden="true" />
                  {fullName(commercial)}
                  <span className="day-person__count">
                    {daySlots.length} {t('agenda.availableSlots').toLowerCase()}
                  </span>
                </p>
                <div className="slot-day__grid">
                  {daySlots.map((item) => (
                    <button
                      key={item.start}
                      type="button"
                      className="slot"
                      onClick={() => setChoice({ commercial, slot: item })}
                    >
                      {formatTime(item.start, locale)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        ) : null}

        {day && choice ? (
          <div className="booking-form">
            <p className="modal__lead">
              {fullName(choice.commercial)} · {formatTime(choice.slot.start, locale)} -{' '}
              {formatTime(choice.slot.end, locale)}
            </p>

            <Field label={t('agenda.meetingTitle')} htmlFor="rdv-title" required>
              <TextInput id="rdv-title" value={form.title} onChange={(value) => setForm({ ...form, title: value })} />
            </Field>

            <Field label={t('customers.title')} htmlFor="rdv-customer">
              <Select
                id="rdv-customer"
                value={form.customerId}
                onChange={(value) => setForm({ ...form, customerId: value })}
                placeholder={t('common.none')}
                options={(customers?.items ?? []).map((item) => ({ value: item.id, label: item.companyName }))}
              />
            </Field>

            <Field label={t('agenda.channel')} htmlFor="rdv-channel">
              <Select
                id="rdv-channel"
                value={form.channel}
                onChange={(value) => setForm({ ...form, channel: value })}
                options={CHANNELS.map((channel) => ({ value: channel, label: t(`agenda.channels.${channel}`) }))}
              />
            </Field>

            <Field label={t('agenda.location')} htmlFor="rdv-location">
              <TextInput
                id="rdv-location"
                value={form.location}
                onChange={(value) => setForm({ ...form, location: value })}
              />
            </Field>

            <Field label={t('common.notes')} htmlFor="rdv-notes">
              <TextArea id="rdv-notes" value={form.notes} onChange={(value) => setForm({ ...form, notes: value })} />
            </Field>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default BookMeetingPage;
